import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';

import { useScreenContext } from 'navigation/context/ScreenContextProvider';
import { AppActions, AppSelectors } from 'store/App';
import {
  getEffectiveTabBar,
  TAB_BARS,
  TAB_BARS_LIST,
} from 'store/App/constants';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { OrderSelectors } from 'store/Orders';
import { SettingSelectors } from 'store/Settings';
import { AnalyticsEvents, useLogEventCallback } from 'util/analytics';

import { NavigationButton } from './NavigationButtons';
import { commonStyles } from './styles';

export function TabBarSection() {
  const { screen } = useScreenContext();
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const logEvent = useLogEventCallback();

  const selectedTabBar = useAppSelector(AppSelectors.selectCurrentTabBar);
  const isSidebarMinimized = useAppSelector(
    AppSelectors.selectIsSidebarMinimized,
  );

  const onSelectTabBar = useCallback(
    (tabBar: (typeof TAB_BARS_LIST)[number]) => {
      dispatch(AppActions.setTabBar(tabBar));
      dispatch(AppActions.setSelectedOrderUUID({ orderUUID: undefined }));
    },
    [dispatch],
  );

  const sidebarTabOrderCountsByScreen = useAppSelector(
    OrderSelectors.selectSidebarTabOrderCountsByScreen,
  );
  const orderCountByStatus = sidebarTabOrderCountsByScreen[screen];

  const cardViewOrganisation = useAppSelector(
    SettingSelectors.selectCardViewLayoutStatusTab,
  );

  type TabBarValue = (typeof TAB_BARS_LIST)[number]['value'];
  const selectedTabBarList = useMemo(
    () =>
      screen === 'chef'
        ? TAB_BARS_LIST.filter((tabBar) =>
            (
              [
                ...((cardViewOrganisation === 'classic'
                  ? ['new', 'cooking']
                  : ['newAndCooking']) satisfies TabBarValue[]),
                'prepared',
                'scheduled',
              ] satisfies TabBarValue[] as unknown[]
            ).includes(tabBar.value),
          )
        : TAB_BARS_LIST.filter((tabBar) =>
            (
              [
                'new',
                'cooking',
                'prepared',
                'scheduled',
              ] satisfies TabBarValue[] as unknown[]
            ).includes(tabBar.value),
          ),
    [cardViewOrganisation, screen],
  );

  // NOTE: Sync
  useEffect(() => {
    const effectiveTabBar = getEffectiveTabBar(
      selectedTabBar,
      screen,
      cardViewOrganisation,
    );

    if (effectiveTabBar !== selectedTabBar) {
      onSelectTabBar(TAB_BARS[effectiveTabBar]);
    }
  }, [onSelectTabBar, screen, selectedTabBar, cardViewOrganisation]);

  return (
    <View style={[commonStyles.container, styles.container]}>
      {selectedTabBarList.map((tabBar) => {
        return (
          <NavigationButton
            key={tabBar.value}
            label={t(
              !isSidebarMinimized
                ? tabBar.labelKey
                : `${tabBar.labelKey}_short`,
              {
                count: orderCountByStatus[tabBar.value]?.[0],
                count2: orderCountByStatus[tabBar.value]?.[1],
              },
            )}
            onPress={() => {
              onSelectTabBar(tabBar);
              logEvent(
                AnalyticsEvents.tabChangedInOverviewScreen({
                  tab: tabBar.value,
                }),
              );
            }}
            isMinimized={isSidebarMinimized}
            iconName={tabBar.iconName}
            isSelected={tabBar.value === selectedTabBar}
            activeColor={tabBar.color}
            isShownBadge={Boolean(
              tabBar.value === TAB_BARS.scheduled.value &&
                orderCountByStatus[tabBar.value]?.[0],
            )}
          />
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginTop: 12,
  },
});
